import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import {
  AdminSetPasswordSchema,
  CreateUserSchema,
  UpdateUserSchema,
  UserSchema,
  type AdminSetPasswordInput,
  type CreateUserInput,
  type UpdateUserInput,
  type User,
} from '@datazo/contracts';
import { apiFetch } from '@/shared/api/client';

const UserListResponseSchema = z.object({
  items: z.array(UserSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
});

export type UserListResponse = z.infer<typeof UserListResponseSchema>;

export interface UsersListParams {
  page: number;
  pageSize: number;
  q?: string;
}

export const usersKeys = {
  all: ['users'] as const,
  list: (params: UsersListParams) => ['users', 'list', params] as const,
};

export function useUsersList(
  params: UsersListParams,
): UseQueryResult<UserListResponse, Error> {
  return useQuery({
    queryKey: usersKeys.list(params),
    queryFn: async () => {
      const qs = new URLSearchParams({
        page: String(params.page),
        pageSize: String(params.pageSize),
      });
      if (params.q !== undefined && params.q.trim() !== '') {
        qs.set('q', params.q.trim());
      }
      return apiFetch<UserListResponse>(`/api/users?${qs.toString()}`, {
        parseAs: UserListResponseSchema,
      });
    },
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

export function useCreateUser(): UseMutationResult<User, Error, CreateUserInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateUserInput) => {
      const body = CreateUserSchema.parse(input);
      return apiFetch<User>('/api/users', {
        method: 'POST',
        body,
        parseAs: UserSchema,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useUpdateUser(): UseMutationResult<
  User,
  Error,
  { id: string; input: UpdateUserInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }) => {
      const body = UpdateUserSchema.parse(input);
      return apiFetch<User>(`/api/users/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body,
        parseAs: UserSchema,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useSetUserPassword(): UseMutationResult<
  void,
  Error,
  { id: string; input: AdminSetPasswordInput }
> {
  return useMutation({
    mutationFn: async ({ id, input }) => {
      const body = AdminSetPasswordSchema.parse(input);
      return apiFetch<void>(`/api/users/${encodeURIComponent(id)}/password`, {
        method: 'POST',
        body,
      });
    },
  });
}

export type { User };
